import { useState } from 'react'
import type { FlowTrigger } from '@cogo/types-v2'
import { mcpBus } from '@/mcp/studio-mcp-bus'
import { toast } from '@/store/toast-store'
import { cn } from '@/lib/utils'

const FLOW_TRIGGERS: FlowTrigger[] = ['event', 'init', 'manual', 'lifecycle']

interface Props {
  onClose: () => void
  compact?: boolean
  /**
   * Opt-in `data-testid` prefix (`<prefix>-name-input`, `-trigger-select`,
   * `-submit`, `-cancel`). Omitted by default so the toolbar instance keeps
   * rendering exactly the attributes it always did: with two of these forms
   * openable on one Behavior screen, unconditional testids would collide.
   * The EVENT HIERARCHY instance passes `flow-create`, the prefix its own
   * (now deleted) copy of this form used to own and that e2e still locates
   * it by.
   */
  testIdPrefix?: string
}

export function CreateFlowForm({ onClose, compact = false, testIdPrefix }: Props) {
  const testId = (suffix: string) =>
    testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined
  const [name, setName] = useState('')
  const [trigger, setTrigger] = useState<FlowTrigger>('event')
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) {
      toast.error('Flow name is required')
      return
    }

    setIsSubmitting(true)
    const result = await mcpBus.execute({
      tool: 'cogo.studio.addFlow',
      params: { name: trimmed, trigger },
    })
    setIsSubmitting(false)

    if (!result.success) {
      toast.error(result.error ?? 'Failed to create flow')
      return
    }

    onClose()
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'space-y-2 border-b border-border bg-accent/30 px-3 py-2',
        compact && 'rounded-md border',
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          New Flow
        </span>
        <button
          type="button"
          data-testid={testId('cancel')}
          onClick={onClose}
          className="text-[10px] text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>

      <input
        data-testid={testId('name-input')}
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Flow name"
        className="h-7 w-full rounded border border-border bg-background px-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-ring"
      />

      <select
        data-testid={testId('trigger-select')}
        value={trigger}
        onChange={(e) => setTrigger(e.target.value as FlowTrigger)}
        className="h-7 w-full rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {FLOW_TRIGGERS.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>

      <button
        type="submit"
        data-testid={testId('submit')}
        disabled={!name.trim() || isSubmitting}
        className={cn(
          'h-7 w-full rounded text-xs font-medium transition-colors',
          name.trim() && !isSubmitting
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'cursor-not-allowed bg-muted text-muted-foreground',
        )}
      >
        {isSubmitting ? 'Creating...' : 'Create Flow'}
      </button>
    </form>
  )
}
